/**
 * useSubscription Hook — Recurring Wallet Top-Up
 *
 * Manages the current user's recurring top-up state.
 * Provides methods to create, update, cancel, resume, and open the billing portal.
 *
 * There are NO plan tiers — subscription is just a recurring wallet charge.
 */

import { useState, useEffect, useCallback } from 'react'
import { useSession } from '@/lib/auth-client'
import { subscriptionAPI, type RecurringTopUpData } from '@/lib/api/subscription'

export interface UseSubscriptionReturn {
  /** Current recurring top-up data, or null if none active */
  subscription: RecurringTopUpData | null
  isLoading: boolean
  error: string | null
  refetch: () => void
  /** Start a new recurring top-up via Stripe Checkout */
  createRecurringTopUp: (monthlyAmount: number) => Promise<void>
  /** Change the monthly recurring amount */
  updateAmount: (monthlyAmount: number) => Promise<void>
  /** Cancel recurring top-up at period end */
  cancelRecurringTopUp: () => Promise<void>
  /** Resume a canceled recurring top-up */
  resumeRecurringTopUp: () => Promise<void>
  /** Open Stripe Customer Portal */
  openBillingPortal: () => Promise<void>
  isCreating: boolean
  isUpdating: boolean
  isCanceling: boolean
  isResuming: boolean
  /** Recurring payment has failed but is still in retry period */
  isPastDue: boolean
  /** All payment retries exhausted — recurring top-up suspended */
  isUnpaid: boolean
}

export function useSubscription(): UseSubscriptionReturn {
  const session = useSession()
  const isAuthenticated = !!session.data?.user

  const [subscription, setSubscription] = useState<RecurringTopUpData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isCanceling, setIsCanceling] = useState(false)
  const [isResuming, setIsResuming] = useState(false)

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscription(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const data = await subscriptionAPI.getSubscription(true)
      setSubscription(data.subscription)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load subscription'
      setError(message)
      console.error('[useSubscription] fetch failed:', err)
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => {
    void fetchSubscription()
  }, [fetchSubscription])

  useEffect(() => {
    const refresh = () => {
      void fetchSubscription()
    }
    window.addEventListener('genie:billing-changed', refresh)
    return () => window.removeEventListener('genie:billing-changed', refresh)
  }, [fetchSubscription])

  const createRecurringTopUp = useCallback(
    async (monthlyAmount: number) => {
      if (!isAuthenticated) return

      setIsCreating(true)
      setError(null)

      try {
        const { checkoutUrl } = await subscriptionAPI.createRecurringTopUp(monthlyAmount, true)
        if (!checkoutUrl) throw new Error('Checkout is unavailable. Please try again.')
        window.location.href = checkoutUrl
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to start checkout'
        setError(message)
        console.error('[useSubscription] createRecurringTopUp failed:', err)
        throw err
      } finally {
        setIsCreating(false)
      }
    },
    [isAuthenticated]
  )

  const updateAmount = useCallback(
    async (monthlyAmount: number) => {
      if (!isAuthenticated) return

      setIsUpdating(true)
      setError(null)

      try {
        const result = await subscriptionAPI.updateAmount(monthlyAmount, true)
        // Refresh to get full subscription data
        if (result.subscription) {
          await fetchSubscription()
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update amount'
        setError(message)
        console.error('[useSubscription] updateAmount failed:', err)
        throw err
      } finally {
        setIsUpdating(false)
      }
    },
    [isAuthenticated, fetchSubscription]
  )

  const cancelRecurringTopUp = useCallback(async () => {
    if (!isAuthenticated) return

    setIsCanceling(true)
    setError(null)

    try {
      await subscriptionAPI.cancelRecurringTopUp(true)
      await fetchSubscription()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel recurring top-up'
      setError(message)
      console.error('[useSubscription] cancel failed:', err)
      throw err
    } finally {
      setIsCanceling(false)
    }
  }, [isAuthenticated, fetchSubscription])

  const resumeRecurringTopUp = useCallback(async () => {
    if (!isAuthenticated) return

    setIsResuming(true)
    setError(null)

    try {
      await subscriptionAPI.resumeRecurringTopUp(true)
      await fetchSubscription()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resume recurring top-up'
      setError(message)
      console.error('[useSubscription] resume failed:', err)
      throw err
    } finally {
      setIsResuming(false)
    }
  }, [isAuthenticated, fetchSubscription])

  const openBillingPortal = useCallback(async () => {
    if (!isAuthenticated) return

    setError(null)
    try {
      const { portalUrl } = await subscriptionAPI.getPortalUrl(true)
      if (!portalUrl) throw new Error('Billing portal is unavailable. Please try again.')
      window.location.href = portalUrl
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open billing portal'
      setError(message)
      console.error('[useSubscription] portal failed:', err)
    }
  }, [isAuthenticated])

  const refetch = useCallback(() => {
    void fetchSubscription()
  }, [fetchSubscription])

  const isPastDue = subscription?.status === 'PAST_DUE'
  const isUnpaid = subscription?.status === 'UNPAID'

  return {
    subscription,
    isLoading,
    error,
    refetch,
    createRecurringTopUp,
    updateAmount,
    cancelRecurringTopUp,
    resumeRecurringTopUp,
    openBillingPortal,
    isCreating,
    isUpdating,
    isCanceling,
    isResuming,
    isPastDue,
    isUnpaid,
  }
}
